/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react-hooks/set-state-in-effect */
import { createContext, useContext, useState, useCallback, useMemo, useEffect, type ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { useAuthValue } from './AuthContext'
import { useBranding } from './UiCustomizationContext'
import { MILKYWAY_NAV_PATHS, DATAFLOW_NAV_PATHS } from '../constants/navigation'

export type AppMode = 'milkyway' | 'platform' | 'dataflow'

interface AppModeContextValue {
  appMode: AppMode
  isMilkyway: boolean
  isPlatform: boolean
  isDataflow: boolean
  switchMode: (mode: AppMode) => void
  toggleMode: () => void
}

const STORAGE_KEY = 'tagp-app-mode'

const BUSINESS_USER_ROLES = new Set(['business_user', 'business-user', 'businessuser'])

function isBusinessUserRole(roleName: string): boolean {
  return BUSINESS_USER_ROLES.has(roleName.toLowerCase())
}

function detectDefaultMode(
  roles: { name: string }[] | undefined | null,
  platformOnly: boolean,
): AppMode {
  // Platform-only deployments are locked to the Platform view.
  if (platformOnly) return 'platform'
  if (roles && roles.some((role) => isBusinessUserRole(role.name))) {
    return 'milkyway'
  }
  return 'platform'
}

function readPersistedMode(): AppMode | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'milkyway' || stored === 'platform' || stored === 'dataflow') return stored
  } catch { /* ignore */ }
  return null
}

function persistMode(mode: AppMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode)
  } catch { /* ignore */ }
}

const AppModeContext = createContext<AppModeContextValue | undefined>(undefined)

export function AppModeProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useAuthValue()
  const { platform_only: platformOnly } = useBranding()
  const navigate = useNavigate()
  const location = useLocation()

  const [appMode, setAppMode] = useState<AppMode>(() => {
    const persisted = readPersistedMode()
    if (persisted) return persisted
    return detectDefaultMode(currentUser?.roles, platformOnly)
  })

  // Re-evaluate default when user first loads (roles may arrive after initial render)
  useEffect(() => {
    if (!currentUser?.roles) return
    const persisted = readPersistedMode()
    if (!persisted) {
      setAppMode(detectDefaultMode(currentUser.roles, platformOnly))
    }
  }, [currentUser?.roles, platformOnly])

  // Platform-only deployments: coerce back into the Platform view if the customer
  // config locks it (branding loads after first render, or a stale persisted mode).
  useEffect(() => {
    if (platformOnly && appMode !== 'platform') {
      setAppMode('platform')
      persistMode('platform')
    }
  }, [platformOnly, appMode])

  const switchMode = useCallback(
    (mode: AppMode) => {
      // Platform-only deployments cannot leave the Platform view.
      if (platformOnly && mode !== 'platform') return
      setAppMode(mode)
      persistMode(mode)

      if (mode === 'milkyway') {
        const isValidInMilkyway = MILKYWAY_NAV_PATHS.some(
          (p) => location.pathname === p || location.pathname.startsWith(p + '/')
        )
        if (!isValidInMilkyway) {
          navigate('/ideate', { replace: true })
        }
      } else if (mode === 'dataflow') {
        const isValidInDataflow = DATAFLOW_NAV_PATHS.some(
          (p) => location.pathname === p || location.pathname.startsWith(p + '/')
        )
        if (!isValidInDataflow) {
          navigate('/ontology/migrations', { replace: true, state: { openFirstGlobe: true } })
        }
      } else if (
        location.pathname === '/ideate' ||
        location.pathname.startsWith('/ideate/') ||
        location.pathname === '/parallelx' ||
        location.pathname.startsWith('/parallelx/')
      ) {
        navigate('/', { replace: true })
      }
    },
    [navigate, location.pathname, platformOnly]
  )

  const toggleMode = useCallback(() => {
    // Platform-only deployments have no other mode to cycle into.
    if (platformOnly) return
    const modeOrder: AppMode[] = ['platform', 'milkyway', 'dataflow']
    const currentIdx = modeOrder.indexOf(appMode)
    const nextIdx = (currentIdx + 1) % modeOrder.length
    switchMode(modeOrder[nextIdx])
  }, [appMode, switchMode, platformOnly])

  const value = useMemo<AppModeContextValue>(
    () => ({
      appMode,
      isMilkyway: appMode === 'milkyway',
      isPlatform: appMode === 'platform',
      isDataflow: appMode === 'dataflow',
      switchMode,
      toggleMode,
    }),
    [appMode, switchMode, toggleMode]
  )

  return <AppModeContext.Provider value={value}>{children}</AppModeContext.Provider>
}

export function useAppMode(): AppModeContextValue {
  const context = useContext(AppModeContext)
  if (context === undefined) {
    throw new Error('useAppMode must be used within an AppModeProvider')
  }
  return context
}
